import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { showToast } from '../components/Toast';
import { ApiError } from '../api/client';
import { fbcAssignmentsApi, type FbcPartnerRow, type FbcRef } from '../api/fbcAssignments';

/**
 * Franchise partners & FBCs (#243). FBCs claim the partners they look after,
 * and release ones they've handed on; marketing can assign anyone. That choice
 * drives the FBC portfolio, scorecard, FBC cards and DMR "my partners".
 */

const NAVY = '#1f2a48';
const MUTED = '#64748b';
const BORDER = '#e2e8f0';
const AMBER = '#b45309';

type Filter = 'mine' | 'unassigned' | 'all' | 'changed';

function errText(e: unknown) {
  return e instanceof ApiError ? e.message : 'Something went wrong. Try again in a moment.';
}

/** The distinct effective FBCs across a partner's AFs (usually one). */
function fbcsOf(p: FbcPartnerRow): FbcRef[] {
  const seen = new Map<string, FbcRef>();
  for (const a of p.afs) if (a.fbc) seen.set(a.fbc.email.toLowerCase(), a.fbc);
  return [...seen.values()];
}

const unassigned = (p: FbcPartnerRow) => p.afs.every((a) => !a.fbc);
const changed = (p: FbcPartnerRow) =>
  p.afs.some((a) => a.overridden && a.fbc?.email.toLowerCase() !== a.sourceFbc?.email.toLowerCase());

export default function FbcPartnersPage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['fbc-assignments'], queryFn: fbcAssignmentsApi.list });
  const [filter, setFilter] = useState<Filter | null>(null);
  const [search, setSearch] = useState('');
  const [fbcFilter, setFbcFilter] = useState('');

  const act = useMutation({
    mutationFn: (fn: () => Promise<unknown>) => fn(),
    onSuccess: () => {
      // Portfolio, scorecard, FBC cards and DMR all follow the assignment.
      qc.invalidateQueries();
    },
    onError: (e) => showToast(errText(e), { variant: 'error', durationMs: 5000 }),
  });

  const data = q.data;
  const me = data?.me ?? null;
  const isAdmin = !!data?.canAssignAnyone;
  const isMine = (p: FbcPartnerRow) => !!me && fbcsOf(p).some((f) => f.email.toLowerCase() === me.email.toLowerCase());

  const counts = useMemo(() => {
    const ps = data?.partners ?? [];
    return {
      all: ps.length,
      mine: ps.filter(isMine).length,
      unassigned: ps.filter(unassigned).length,
      changed: ps.filter(changed).length,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  if (q.isLoading) return <Shell><p style={{ color: MUTED }}>Loading franchise partners…</p></Shell>;
  if (q.error || !data) return <Shell><p style={{ color: '#b91c1c' }}>{errText(q.error)}</p></Shell>;

  const active: Filter = filter ?? (isAdmin ? 'all' : 'mine');
  const needle = search.trim().toLowerCase();
  const rows = data.partners.filter((p) => {
    if (active === 'mine' && !isMine(p)) return false;
    if (active === 'unassigned' && !unassigned(p)) return false;
    if (active === 'changed' && !changed(p)) return false;
    if (fbcFilter && !fbcsOf(p).some((f) => f.email === fbcFilter)) return false;
    if (!needle) return true;
    const hay = `${p.name} ${p.afs.map((a) => a.af).join(' ')} ${p.marketingNames.join(' ')} ${fbcsOf(p).map((f) => f.name).join(' ')}`;
    return hay.toLowerCase().includes(needle);
  });

  const tabs: Array<[Filter, string, number]> = isAdmin
    ? [['all', 'All partners', counts.all], ['unassigned', 'Unassigned', counts.unassigned], ['changed', 'Changed from Zoho', counts.changed]]
    : [['mine', 'My partners', counts.mine], ['unassigned', 'Unassigned', counts.unassigned], ['all', 'All partners', counts.all]];

  const claim = (p: FbcPartnerRow) => {
    const others = fbcsOf(p).filter((f) => !me || f.email.toLowerCase() !== me.email.toLowerCase());
    if (others.length && !window.confirm(`Take over ${p.name} from ${others.map((f) => f.name).join(' & ')}?`)) return;
    act.mutate(() => fbcAssignmentsApi.assign(p.key));
  };
  const release = (p: FbcPartnerRow) => {
    if (!window.confirm(`Release ${p.name}? They'll show as unassigned until another FBC claims them.`)) return;
    act.mutate(() => fbcAssignmentsApi.release(p.key));
  };

  return (
    <Shell>
      <h1 style={{ margin: '10px 0 4px', color: NAVY, fontSize: 26 }}>
        {isAdmin ? 'Franchise partners & FBCs' : 'My franchise partners'}
      </h1>
      <p style={{ margin: '0 0 16px', color: MUTED, maxWidth: 760 }}>
        {isAdmin
          ? 'Who looks after each franchise partner. FBCs claim and release their own; you can assign anyone. This drives each FBC’s portfolio, the scorecard, the FBC cards and DMR “my partners”. Zoho isn’t changed.'
          : 'Claim the franchise partners you look after, and release any you’ve handed on. Your portfolio, scorecard and DMR “my partners” follow this list. Zoho isn’t changed.'}
      </p>

      {!isAdmin && !me && (
        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', color: AMBER, padding: '10px 14px', borderRadius: 8, marginBottom: 14 }}>
          Your account isn’t on the FBC list yet, so you can’t claim partners. Ask marketing to add you.
        </div>
      )}

      <div style={{ display: 'flex', gap: 4, borderBottom: `1px solid ${BORDER}`, marginBottom: 14, flexWrap: 'wrap' }}>
        {tabs.map(([k, label, n]) => (
          <button
            key={k}
            onClick={() => setFilter(k)}
            style={{
              background: 'none', border: 'none', padding: '9px 14px', cursor: 'pointer', fontSize: 15,
              fontWeight: active === k ? 700 : 500, color: active === k ? NAVY : MUTED,
              borderBottom: active === k ? `3px solid ${NAVY}` : '3px solid transparent',
            }}
          >
            {label} <span style={{ color: MUTED, fontWeight: 500 }}>({n})</span>
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name, AF number or city…"
          style={{ ...input, maxWidth: 320 }}
        />
        {isAdmin && (
          <select value={fbcFilter} onChange={(e) => setFbcFilter(e.target.value)} style={{ ...input, width: 'auto' }}>
            <option value="">Every FBC</option>
            {data.fbcs.map((f) => (
              <option key={f.email} value={f.email}>{f.name}</option>
            ))}
          </select>
        )}
      </div>

      <div style={{ border: `1px solid ${BORDER}`, borderRadius: 10, overflow: 'auto', background: '#fff' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr>
              {['Franchise partner', 'FBC', ''].map((h) => (
                <th key={h} style={th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => {
              const fbcs = fbcsOf(p);
              const zoho = [...new Set(p.afs.filter((a) => a.overridden).map((a) => a.sourceFbc?.name ?? 'nobody'))];
              const mine = isMine(p);
              const loose = p.afs.filter((a) => !a.fbc);
              const allMine = mine && p.afs.every((a) => a.fbc?.email.toLowerCase() === me!.email.toLowerCase());
              return (
                <tr key={p.key}>
                  <td style={td}>
                    <strong style={{ color: NAVY }}>{p.name}</strong>
                    <div style={{ color: MUTED, fontSize: 12 }}>
                      {p.afs.map((a) => a.af).join(', ')}
                      {p.marketingNames[0] && ` · ${p.marketingNames.join(', ')}`}
                    </div>
                  </td>
                  <td style={td}>
                    {fbcs.length ? (
                      <span style={{ fontWeight: mine ? 700 : 500 }}>{mine ? 'You' : fbcs.map((f) => f.name).join(' & ')}</span>
                    ) : (
                      <span style={{ color: AMBER, fontWeight: 600 }}>Unassigned</span>
                    )}
                    {fbcs.length > 0 && loose.length > 0 && (
                      <div style={{ color: AMBER, fontSize: 12 }}>{loose.map((a) => a.af).join(', ')} unassigned</div>
                    )}
                    {changed(p) && <div style={{ color: MUTED, fontSize: 12 }}>Zoho: {zoho.join(', ')}</div>}
                  </td>
                  <td style={{ ...td, whiteSpace: 'nowrap', textAlign: 'right' }}>
                    {isAdmin ? (
                      <>
                        <select
                          value={fbcs.length === 1 ? fbcs[0].email : ''}
                          disabled={act.isPending}
                          onChange={(e) =>
                            act.mutate(() => fbcAssignmentsApi.assign(p.key, e.target.value === '__none' ? null : e.target.value))
                          }
                          style={{ ...input, width: 'auto' }}
                        >
                          {fbcs.length !== 1 && <option value="">{fbcs.length ? 'Mixed' : 'Assign to…'}</option>}
                          {data.fbcs.map((f) => (
                            <option key={f.email} value={f.email}>{f.name}</option>
                          ))}
                          <option value="__none">Unassigned</option>
                        </select>
                        {p.afs.some((a) => a.overridden) && (
                          <button style={linkBtn} disabled={act.isPending} onClick={() => act.mutate(() => fbcAssignmentsApi.reset(p.key))}>
                            Follow Zoho
                          </button>
                        )}
                      </>
                    ) : allMine ? (
                      <button style={linkBtn} disabled={act.isPending} onClick={() => release(p)}>Release</button>
                    ) : mine ? (
                      <button style={primaryBtn} disabled={act.isPending} onClick={() => claim(p)}>Claim all</button>
                    ) : (
                      me && (
                        <button style={primaryBtn} disabled={act.isPending} onClick={() => claim(p)}>
                          {fbcs.length ? 'Take over' : 'Claim'}
                        </button>
                      )
                    )}
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr>
                <td colSpan={3} style={{ ...td, color: MUTED }}>
                  {active === 'mine' && !needle
                    ? 'You haven’t claimed any partners yet. Open “All partners” or “Unassigned” to claim yours.'
                    : 'No partners match.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ padding: '22px 24px 70px', maxWidth: 1100, margin: '0 auto' }}>
      <p style={{ margin: 0 }}>
        <Link to="/" style={{ color: '#2563eb' }}>← Dashboard</Link>
      </p>
      {children}
    </div>
  );
}

const input: React.CSSProperties = {
  padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 14, width: '100%', boxSizing: 'border-box',
};
const primaryBtn: React.CSSProperties = {
  background: NAVY, color: '#fff', border: 'none', borderRadius: 6, padding: '5px 14px', fontWeight: 600, fontSize: 13, cursor: 'pointer',
};
const linkBtn: React.CSSProperties = {
  background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', fontSize: 13, padding: '0 8px', fontWeight: 600,
};
const th: React.CSSProperties = {
  textAlign: 'left', padding: '9px 12px', borderBottom: `1px solid ${BORDER}`, fontSize: 12, textTransform: 'uppercase',
  letterSpacing: '.05em', color: MUTED, background: '#fafafa',
};
const td: React.CSSProperties = { padding: '9px 12px', borderBottom: '1px solid #f1f5f9', verticalAlign: 'top' };
